import { useTranslation } from "react-i18next";
import { cn, interval } from "../lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";
import { Checkbox } from "./checkbox";
import { CommandItem } from "./command";
import { Icon } from "./icon";
import {
  type LoadingRenderer,
  type Option,
  type OptionRendererProps,
  SearchableSelect,
  type SearchableSelectProps,
} from "./searchable-select";
import { Skeleton } from "./skeleton";

export interface UserData {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string | null;
}

interface UserOption extends Option<string> {
  data: UserData;
}

export interface UserSearchableSelectProps
  extends Omit<
    SearchableSelectProps<string>,
    "options" | "sections" | "optionRenderer" | "defaultValue"
  > {
  users: UserData[];
  value?: string[];
  defaultValue?: UserData;
  onSelect?: (userIds: string[]) => void;
  emptyLabel?: string;
  readonly?: boolean;
}

const UserOptionRenderer = ({
  option,
  isSelected,
  isMulti,
  onSelect,
  readonly,
}: OptionRendererProps<string>) => {
  const userOption = option as UserOption;
  const userData = userOption.data;

  const userContent = (
    <div className="flex items-center w-full">
      <Avatar size="S" className="mr-2">
        {userData?.id === "unassigned" ? (
          <Icon icon="user" className="text-gray-400" size={16} />
        ) : (
          <>
            <AvatarImage src={userData?.avatarUrl || ""} />
            <AvatarFallback
              size="S"
              firstName={userData?.firstName}
              lastName={userData?.lastName}
            />
          </>
        )}
      </Avatar>
      <span className="font-semibold">{option.label}</span>
    </div>
  );

  if (isMulti) {
    return (
      <div className="flex items-center w-full">
        <Checkbox
          checked={isSelected}
          onCheckedChange={onSelect}
          className="mr-2"
        />
        {userContent}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between w-full">
      {userContent}
      {!readonly && isSelected && (
        <Icon icon="check" className="text-primary ml-2" />
      )}
    </div>
  );
};

const SKELETON_ITEM_COUNT = 5;

// Custom loading renderer for user options - matches user option design
const UserLoadingRenderer: LoadingRenderer = () => {
  return (
    <>
      {interval(SKELETON_ITEM_COUNT).map((num) => (
        <CommandItem key={`user-loading-${num}`} disabled>
          <div className="flex items-center w-full py-1">
            {/* Avatar skeleton - matches Avatar size="S" (16x16px) */}
            <Skeleton className="h-4 w-4 rounded-full mr-2 flex-shrink-0" />
            {/* Name skeleton - matches typical name length */}
            <Skeleton className="h-3 w-full rounded" />
          </div>
        </CommandItem>
      ))}
    </>
  );
};

type UserSearchableSelectWithRefProps = UserSearchableSelectProps & {
  ref?: React.Ref<HTMLButtonElement>;
};
export const UserSearchableSelect = ({
  users,
  searchPlaceholder = "Search users...",
  noResultsText = "No users found",
  className,
  onSearch,
  defaultValue,
  emptyLabel = "none",
  readonly = false,
  ref,
  ...props
}: UserSearchableSelectWithRefProps) => {
  const { t } = useTranslation();
  // Transform users to options with embedded data
  const options: UserOption[] = users?.map((user) => ({
    value: user.id,
    label:
      user.id === "unassigned"
        ? t(emptyLabel)
        : `${user.firstName} ${user.lastName}`,
    data: user,
  }));

  return (
    <SearchableSelect
      {...props}
      ref={ref}
      options={options}
      optionRenderer={UserOptionRenderer}
      loadingRenderer={UserLoadingRenderer}
      searchPlaceholder={searchPlaceholder}
      noResultsText={noResultsText}
      className={cn("w-full", className)}
      defaultValue={defaultValue ? [defaultValue.id] : undefined}
      displayValue={
        defaultValue
          ? `${defaultValue.firstName} ${defaultValue.lastName}`
          : undefined
      }
      readonly={readonly}
    />
  );
};
UserSearchableSelect.displayName = "UserSearchableSelect";
